import CryptoJS from 'crypto-js';
import { env } from '@/shared/config/env';

/**
 * Static secret key for encryption.
 * Sourced from the centralised env config (CR017) so the dev/test fallback
 * lives in one place. Protects against:
 * - Casual localStorage inspection
 * - Browser extensions reading data
 * - Physical device access
 */
const SECRET_KEY = env.security.encryptionKey;

/**
 * Encrypt data using AES encryption
 * @param data - Any JSON-serializable data
 * @returns Encrypted string
 */
export const encryptData = (data: unknown): string => {
  try {
    const jsonString = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();
    return encrypted;
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypt data using AES decryption
 * @param encryptedData - Encrypted string
 * @returns Decrypted data or null if decryption fails
 */
export const decryptData = <T = unknown>(encryptedData: string): T | null => {
  try {
    const decrypted = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    const jsonString = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!jsonString) {
      console.error('Decryption failed - invalid data or key mismatch');
      return null;
    }
    
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
};

/**
 * Secure localStorage wrapper with automatic encryption/decryption
 * All data is encrypted before storing and decrypted when retrieving
 */
export const secureStorage = {
  /**
   * Store data in localStorage with encryption
   * @param key - Storage key
   * @param value - Data to store (will be encrypted)
   */
  setItem: (key: string, value: unknown): void => {
    try {
      const encrypted = encryptData(value);
      localStorage.setItem(key, encrypted);
      localStorage.setItem(`${key}_enc`, '1'); // Mark as encrypted
    } catch (error) {
      console.error(`Failed to securely store ${key}:`, error);
    }
  },

  /**
   * Retrieve and decrypt data from localStorage
   * @param key - Storage key
   * @returns Decrypted data or null if not found/decryption fails
   */
  getItem: <T = unknown>(key: string): T | null => {
    try {
      const data = localStorage.getItem(key);
      if (!data) return null;
      
      const isEncrypted = localStorage.getItem(`${key}_enc`) === '1';
      
      if (isEncrypted) {
        return decryptData<T>(data);
      } else {
        // Handle unencrypted legacy data
        try {
          return JSON.parse(data) as T;
        } catch {
          return null;
        }
      }
    } catch (error) {
      console.error(`Failed to retrieve ${key}:`, error);
      return null;
    }
  },

  /**
   * Remove item from localStorage
   * @param key - Storage key
   */
  removeItem: (key: string): void => {
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_enc`);
  },

  /**
   * Clear all localStorage data
   */
  clear: (): void => {
    localStorage.clear();
  }
};

